// src/components/SchemaBuilderApp.tsx
import { Button } from '@commenda/kakashi';
import { TJsonSchemaDocument } from '@commenda/types';
import { useEffect, useMemo, useState } from 'react';
import {
  defaultJsonSchemaDocument,
  JsonSchemaConverterHelper,
} from '../helpers';
import AdvancedEditor from './AdvancedEditor';
import SimpleEditor from './SimpleEditor';

export const JsonSchemaBuilder = ({
  initialJsonSchema,
  setJsonSchema,
  showAdditionalProperties = false,
  hideUpdateButton = false,
  onSchemaChange,
}: {
  initialJsonSchema: Record<string, any>;
  setJsonSchema: (jsonSchema: Record<string, any>) => void;
  showAdditionalProperties?: boolean;
  hideUpdateButton?: boolean;
  onSchemaChange?: (schema: Record<string, unknown>) => void;
}) => {
  const [mode, setMode] = useState<'simple' | 'advanced'>('simple');
  const [jsonSchemaDoc, setJsonSchemaDoc] = useState<TJsonSchemaDocument>(
    defaultJsonSchemaDocument()
  );

  const jsonSchema = useMemo(
    () => JsonSchemaConverterHelper.buildJSONSchema(jsonSchemaDoc),
    [jsonSchemaDoc]
  );

  useEffect(() => {
    setJsonSchemaDoc(
      JsonSchemaConverterHelper.importFromJSONSchema(initialJsonSchema)
    );
  }, [initialJsonSchema]);

  useEffect(() => {
    onSchemaChange?.(jsonSchema);
  }, [jsonSchema, onSchemaChange]);

  return (
    <div className="bg-white p-6 rounded-3xl">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex items-center justify-end">
          <div className="flex rounded-xl overflow-hidden border border-border-primary text-white">
            <div
              className={`px-4 py-2 txt-extra-small-regular cursor-pointer ${mode === 'simple' ? 'bg-button-primary-bg' : 'hover:bg-button-secondary-bg-hover text-button-secondary-text'}`}
              onClick={() => setMode('simple')}
            >
              Simple
            </div>
            <div
              className={`px-4 py-2 txt-extra-small-regular cursor-pointer ${mode === 'advanced' ? 'bg-button-primary-bg' : 'hover:bg-button-secondary-bg-hover text-button-secondary-text'}`}
              onClick={() => setMode('advanced')}
            >
              Advanced
            </div>
          </div>
        </div>

        {mode === 'simple' ? (
          <SimpleEditor
            schemaDocument={jsonSchemaDoc}
            onSchemaDocumentChange={setJsonSchemaDoc}
            showAdditionalProperties={showAdditionalProperties}
          />
        ) : (
          <AdvancedEditor
            schemaDocument={jsonSchemaDoc}
            onSchemaDocumentChange={setJsonSchemaDoc}
          />
        )}

        {!hideUpdateButton && (
          <div className="pt-4 flex items-center justify-end gap-3">
            <Button size="md" onClick={() => setJsonSchema(jsonSchema)}>
              Update
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
